/* eslint-disable @typescript-eslint/prefer-regexp-exec */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useCallback, useMemo, useState } from 'react';
import * as yup from 'yup';
import { CircularProgress } from '@material-ui/core';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { FormApi } from 'final-form';
import { IApiResponse } from 'utils/models';
import debounce from 'utils/debounce';
import api from 'api/api';
import { mapYupObjectError, objectMap } from 'utils/helpers';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import BulksWrapper from 'components/modules/Beneficiaries/BulksModules/BulksWrapper/BulksWrapper';
import Form from 'ui/Form/Form';
import Input from 'ui/Input/Input';
import { AxiosStatus } from 'api/axiosConfig';
import noop from 'utils/noop';
import { requiredOnly } from 'utils/validation';
import Button from 'ui/Button/Button';
import Loader from 'ui/Loader/Loader';
import { onlyNCharacters, onlyNumbers } from 'utils/formatters';
import { StatusModal, WarningModal } from 'components/modals';

import { IApiPrepaidCardBen } from 'components/modules/Beneficiaries/Beneficiaries.model';
import { ITransformedPrepaidBulkId } from './BulkPrepaidBen.model';

import styles from './EditPrepaidBulkBen.module.scss';

import plus from 'assets/icons/plus.svg';
import trash from 'assets/icons/trash.png';

type FormType = FormApi<IFormValues, Partial<IFormValues>>;

export interface IFormValues {
  beneficiaryData: {
    cardCustomerId: string;
    cardName: string;
    phoneNumber: string;
    last4digit: string;
  }[];
}

const schema = yup.object().shape({
  beneficiaryData: yup.array().of(
    yup.object().shape({
      cardCustomerId: requiredOnly('Card Customer Id'),
      cardName: requiredOnly('Card Name'),
      phoneNumber: requiredOnly('Phone Number'),
      last4digit: requiredOnly('Card’s last 4 digits').min(4, '4 digits required'),
    }),
  ),
});

const validate = (values: IFormValues) => {
  try {
    schema.validateSync(values, { abortEarly: false });
  } catch (e) {
    const valErrs = mapYupObjectError<IFormValues>(e);
    return objectMap(valErrs, (v) => v[0]);
  }
  return undefined;
};

interface IEditPrepaidBulkBen {
  data: ITransformedPrepaidBulkId;
  onClose: () => void;
}

const EditPrepaidBulkBen = ({ data, onClose }: IEditPrepaidBulkBen) => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [showWarning, setShowWarning] = useState(false);
  const [submitWarningStatus, setSubmitWarningStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const warningSubmitting = submitWarningStatus === AxiosStatus.PENDING;
  const [showStatus, setShowStatus] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const onCloseStatusModal = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setShowStatus(false);
    setSubmitWarningStatus(AxiosStatus.DEFAULT);
    onClose();
  };

  const [isLookingUp, setLookingUp] = useState(false);
  const [lookingIndex, setLookingIndex] = useState<number | null>(null);
  const [lookingErrorText, setLookingErrorText] = useState<Record<string, string>>({});
  const updateLookingErrorText = (update: Record<string, string>) =>
    setLookingErrorText((current) => ({ ...current, ...update }));
  const removeKeyFromLookingErrorText = (keyToRemove: string) => {
    setLookingErrorText((current) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [keyToRemove]: removedKey, ...rest } = current;
      return rest;
    });
  };
  const removeBeneficiaryAtIndex = (indexToRemove: number) => {
    setLookingErrorText((current) => {
      const updatedState = { ...current };

      Object.keys(updatedState).forEach((key) => {
        const match = key.match(/^beneficiaryData\[(\d+)\]\.beneficiaryWalletNumber$/);
        if (match) {
          const currentIndex = parseInt(match[1], 10);
          if (currentIndex === indexToRemove) {
            delete updatedState[key];
          } else if (currentIndex > indexToRemove) {
            const newIndex = currentIndex - 1;
            const newKey = `beneficiaryData[${newIndex}].beneficiaryWalletNumber`;
            updatedState[newKey] = updatedState[key];
            delete updatedState[key];
          }
        }
      });

      return updatedState;
    });
  };

  let mainForm!: FormType;

  const initialValues: IFormValues = useMemo(() => {
    const beneficiaryDataArray = data.beneficiaries.map((beneficiary) => ({
      cardCustomerId: beneficiary.cardCustomerId,
      cardName: beneficiary.cardName,
      phoneNumber: beneficiary.phoneNumber,
      last4digit: beneficiary.last4digit,
    }));

    const iniitalData = {
      beneficiaryData: beneficiaryDataArray,
    };

    return iniitalData;
  }, []);

  const lookupAccount = (form: FormType, customerId: string, index: number) => {
    api
      .get(`/transfer/validatePrepaidCard?customerId=${String(customerId)}`, {}, true, true)
      .then((response: IApiPrepaidCardBen) => {
        setLookingUp(false);
        form.mutators.changeFieldValue(`beneficiaryData[${index}].cardName`, response.customerResponse.preferredNameField);
        form.mutators.changeFieldValue(`beneficiaryData[${index}].phoneNumber`, response.customerResponse.mobilePhoneNumberField);
        removeKeyFromLookingErrorText(`beneficiaryData[${index}].cardCustomerId`);
      })
      .catch((error: IApiResponse) => {
        console.error(error);
        setLookingUp(false);
        updateLookingErrorText({ [`beneficiaryData[${index}].cardCustomerId`]: 'Account not found' });
        form.mutators.changeFieldValue(`beneficiaryData[${index}].cardName`, '');
        form.mutators.changeFieldValue(`beneficiaryData[${index}].phoneNumber`, '');
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(debounce(lookupAccount, 500), []);

  const handleChangeCustomerId = (form: FormType, index: number) => ({ target }) => {
    const previousValue = form.getFieldState(target.name as 'beneficiaryData')?.initial;
    const currentValue = form.getFieldState(target.name as 'beneficiaryData')?.value;
    if (previousValue !== currentValue && target.value) {
      setLookingUp(true);
      setLookingIndex(index);
      debounced(form, target.value as string, index);
    }
  };

  const handleDeleteBen = () => {
    setShowWarning(true);
  };

  const handleAddBen = () => {
    mainForm.mutators.push('beneficiaryData', '');
  };

  const checkFieldError = (errors, fieldName: string) => {
    if (errors[fieldName]) {
      return true;
    }
    return false;
  };

  const errorText = (errors, fieldName: string) => {
    if (errors[fieldName]) {
      return errors[fieldName] as string;
    }
    return '';
  };

  const checkCardIdFieldError = (errors, fieldName: string) => {
    if (errors[fieldName] || Object.prototype.hasOwnProperty.call(lookingErrorText, fieldName)) {
      return true;
    }
    return false;
  };


  const errorCardIdText = (errors, fieldName: string) => {
    if (Object.prototype.hasOwnProperty.call(lookingErrorText, fieldName)) {
      return lookingErrorText[fieldName];
    }
    if (errors[fieldName]) {
      return errors[fieldName] as string;
    }
    return '';
  };

  const onSubmit = (values: IFormValues) => {
    setSubmitStatus(AxiosStatus.PENDING);
    const postBody = {
      bulkId: data.id,
      prepaidCardBeneficiaryEntries: values.beneficiaryData,
    };
    api
      .post('/beneficiary/prepaidcard/updateBulk', postBody, false, true)
      .then((response: IApiResponse) => {
        setSuccessMessage(response.description);
      })
      .catch((error: IApiResponse) => {
        console.error(error.description);
        setErrorMessage(error.description);
      })
      .finally(() => {
        setSubmitStatus(AxiosStatus.DEFAULT);
        setShowStatus(true);
      });
  };

  const onSubmitWarning = () => {
    setSubmitWarningStatus(AxiosStatus.PENDING);
    api
      .post('/beneficiary/prepaidcard/deleteBulk', { bulkId: data.id })
      .then((response: IApiResponse) => {
        setSubmitWarningStatus(AxiosStatus.SUCCESS);
        setSuccessMessage(response.description);
      })
      .catch((error: IApiResponse) => {
        setSubmitWarningStatus(AxiosStatus.FAILURE);
        setErrorMessage(error.description);
      })
      .finally(() => {
        setShowWarning(false);
        setShowStatus(true);
      });
  };

  const closeWarning = () => {
    setShowWarning(false);
  };

  const loadingRow = (index: number) => isLookingUp && lookingIndex === index;

  return (
    <BulksWrapper onClickBack={onClose} onDeleteBen={handleDeleteBen}>
      {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={validate}
        mutators={{ changeFieldValue, setFieldTouched, ...arrayMutators }}
        render={({ handleSubmit, form, errors, hasValidationErrors }) => {
          mainForm = mainForm || form;
          return (
            <form onSubmit={handleSubmit}>
              <FieldArray name="beneficiaryData">
                {({ fields }) => (
                  <>
                    {fields.map((name: string, index: number) => (
                      <div key={name} className={styles.bulkRow}>
                        <div className={styles.bulkFields}>
                          <Input
                            name={`${name}.cardCustomerId`}
                            label="Customer ID"
                            disabled={submitting || isLookingUp}
                            onBlur={handleChangeCustomerId(form, index)}
                            fieldProps={{
                              parse: (value: string) => onlyNCharacters(10)(onlyNumbers(value)),
                            }}
                            className={styles.formField}
                            error={checkCardIdFieldError(errors, `${name}.cardCustomerId`)}
                            helperText={errorCardIdText(errors, `${name}.cardCustomerId`)}
                          />
                          <Input
                            name={`${name}.cardName`}
                            label="Card Name"
                            disabled
                            className={styles.formField}
                            error={checkFieldError(errors, `${name}.cardName`)}
                            // helperText={errorCustomerIdNameText(errors, index, `${name}.cardName`)}
                            helperText={errorText(errors, `${name}.cardName`)}
                          />
                          <Input
                            name={`${name}.phoneNumber`}
                            label="Phone Number"
                            disabled
                            className={styles.formField}
                            error={checkFieldError(errors, `${name}.phoneNumber`)}
                            // helperText={errorCustomerIdNameText(errors, index, `${name}.phoneNumber`)}
                            helperText={errorText(errors, `${name}.phoneNumber`)}
                          />
                          <Input
                            name={`${name}.last4digit`}
                            label="Card’s last 4 digits"
                            disabled={submitting}
                            fieldProps={{
                              parse: (value: string) => onlyNCharacters(4)(onlyNumbers(value)),
                            }}
                            className={styles.formField}
                            error={checkFieldError(errors, `${name}.last4digit`)}
                            helperText={errorText(errors, `${name}.last4digit`)}
                          />
                        </div>
                        {loadingRow(index) && <Loader size={40} />}
                        {!loadingRow(index) && fields.length && fields.length > 1 ? (
                          <>
                            <div
                              className={styles.removeRowBtn}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                fields.remove(index);
                                removeBeneficiaryAtIndex(index);
                              }}
                              onKeyPress={noop}
                            >
                              <img src={trash} alt="delete icon" width="40" height="40" />
                            </div>

                            <Button
                              className={styles.deleteBeneficiaryMobileBtn}
                              onClick={() => {
                                fields.remove(index);
                                removeBeneficiaryAtIndex(index);
                              }}
                            >
                              Delete Beneficiary
                            </Button>
                          </>
                        ) : null}
                      </div>
                    ))}
                    <button type="button" className={styles.addBeneficiaryBtn} onClick={handleAddBen}>
                      <img src={plus} alt="add icon" width="12" height="12" />
                      Add one more beneficiary
                    </button>
                    <Button
                      type="submit"
                      className={styles.submitBtn}
                      disabled={hasValidationErrors || !form.getState().dirty || isLookingUp || submitting}
                    >
                      {submitting ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </Button>
                  </>
                )}
              </FieldArray>
              <WarningModal
                open={showWarning}
                submitName="Delete"
                submitting={warningSubmitting}
                onSubmit={onSubmitWarning}
                onClose={closeWarning}
              >
                Are you sure you want to delete the Bulk Beneficiary List?
              </WarningModal>
              <StatusModal
                open={showStatus}
                status={successMessage ? 'success' : 'failure'}
                onRetry={onCloseStatusModal}
                onClose={onCloseStatusModal}
                onRetryText="Continue"
              >
                {successMessage || errorMessage}
              </StatusModal>
            </form>
          );
        }}
      />
    </BulksWrapper>
  );
};

export default EditPrepaidBulkBen;
